<template>
  <div>
    <AdminOrderAlarmModal ref="adminOrderAlarmUpdate" @orderUpdated="getAdminOrderAlarmLog()" :unit_id="null" :sellers="sellers" :alarm_centers="alarm_centers" :customer="customer" :unit="unit" :updateInfo="updateInfo" />
    <AdminOrderAlarmLogModal :log_info="log_info" :customers="customers" :units="units" :sellers="sellers" :alarm_centers="boardAlarmCenters" />
    <PackingChecklistModal :order="pendingChecklistMove && pendingChecklistMove.order" @confirm="confirmPackingChecklist" @cancel="cancelPackingChecklist" />
    <modal name="modal-export-archive" class="modal-export-archive modal-inner" transition="pop-out" :width="380" height="auto" :adaptive="true" :clickToClose="false">
      <span class="close-button" @click="$modal.hide('modal-export-archive')"><BaseIcon icon="times-circle" class="text-white" /></span>
      <div class="modal-header">
        {{ $t('order_log.export_archive') }}
      </div>
      <div class="modal-body">
        <p class="text-sm text-gray-600 mb-4">{{ $t('order_log.export_format_question') }}</p>
        <div class="flex">
          <button class="btn-blue w-1/2 py-3 mr-2" @click="chooseExportFormat('xlsx')">Excel (.xlsx)</button>
          <button class="btn-blue-outline w-1/2 py-3" @click="chooseExportFormat('csv')">CSV (.csv)</button>
        </div>
        <div class="w-full text-center mt-4 mb-2">
          <button class="text-sm text-gray-500 underline" @click="$modal.hide('modal-export-archive')">{{ $t('order_log.cancel') }}</button>
        </div>
      </div>
    </modal>
    <div class="toggelbtn mb-3 flex justify-end">
      <button v-if="archive" class="btn-blue-outline mb-3 mr-2" :disabled="exportLoading" @click="exportArchivedOrders">
        {{ exportLoading ? $t('order_log.loading') : $t('order_log.export_archive') }}
      </button>
      <button class="btn-blue-outline mb-3 float-right" @click="toggleArchive">
        {{ archive ? $t('order_log.show_orders') : $t('order_log.show_archive') }}
      </button>
    </div>
    
    <!-- Custom search input for archive -->
    <div class="mb-3" v-show="archive">
      <input
        type="text"
        v-model="archiveSearchTerm"
        @input="debouncedSearch"
        :placeholder="$t('order_log.search_placeholder')"
        class="w-full md:w-1/2 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
      />
    </div>
    
    <div class="order-archive" v-show="archive">
      <div v-if="archiveLoading" class="text-center py-8 text-gray-500">
        {{ $t('order_log.loading') }}
      </div>
      <vue-good-table
        v-else
        styleClass="vgt-table"
        row-style-class="text-sm"
        :columns="archivedColumns"
        :rows="filteredArchivedOrders"
        :search-options="{ enabled: false }"
        :pagination-options="{ enabled: false }"
      >
        <template slot="table-row" slot-scope="props">
          <span v-if="props.column.field === 'customer_name'"> 
            <a @click.prevent="showAdminOrderLogModal(props.row)" class="text-blue-600 underline cursor-pointer">
              {{ props.row.customer_name }}
            </a>
            <span class="cursor-pointer" @click.prevent="showAdminOrderAlarmModal(props.row)">
              <BaseIcon icon="pencil-alt" class="text-xxs ml-2 text-primary-500" />
          </span>
          </span>
          
          <span v-else-if="props.column.field === 'unit_display_name'" :title="props.row.unit_display_name">
            {{ props.formattedRow[props.column.field] }}
          </span>

          <span v-else>
            {{ props.formattedRow[props.column.field] }}
          </span>
        </template>
      </vue-good-table>
      <!-- Server-side pagination controls -->
      <div v-if="!archiveLoading && archivePagination.totalPages > 1" class="flex items-center justify-between mt-4 px-2">
        <span class="text-sm text-gray-600">
          {{ $t('order_log.showing') }} {{ ((archivePagination.page - 1) * archivePagination.limit) + 1 }}–{{ Math.min(archivePagination.page * archivePagination.limit, archivePagination.total) }} {{ $t('order_log.of') }} {{ archivePagination.total }}
        </span>
        <div class="flex space-x-2">
          <button
            class="btn-blue-outline px-3 py-1 text-sm"
            :disabled="archivePagination.page <= 1"
            @click="fetchArchivedOrders(archivePagination.page - 1)"
          >
            &laquo; {{ $t('order_log.prev') }}
          </button>
          <button
            class="btn-blue-outline px-3 py-1 text-sm"
            :disabled="archivePagination.page >= archivePagination.totalPages"
            @click="fetchArchivedOrders(archivePagination.page + 1)"
          >
            {{ $t('order_log.next') }} &raquo;
          </button>
        </div>
      </div>
    </div>

    <div class="admin-order-alarm" v-show="!archive">
      <div class="my-8 rounded-xl">
        <!-- Rows are merged on the product `type` (same device created in the SE and
             NO portals has different names/ids but the same type). The checkboxes
             double as a filter: with everything ticked nothing is filtered; once
             something is unticked, only orders containing a ticked product are shown. -->
        <table class="min-w-full table-auto text-sm border border-gray-300 rounded-xl">
          <thead class="bg-gray-100">
            <tr>
              <th class="px-2 py-2 border w-10 text-center">
                <input type="checkbox" class="form-checkbox h-4 w-4 cursor-pointer" :checked="unchecked_product_types.length === 0" :indeterminate.prop="unchecked_product_types.length > 0 && checkedProductTypes.length > 0" @change="toggleAllProducts($event.target.checked)" />
              </th>
              <th class="px-4 py-2 border">{{ $t('order_log.product') }}</th>
              <th class="px-4 py-2 border">{{ $t('order_log.stock') }}</th>
              <th class="px-4 py-2 border">{{ $t('order_log.ordered') }}</th>
              <th class="px-4 py-2 border">{{ $t('order_log.difference') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in stockList" :key="item.type" class="border-b" :class="{ 'opacity-50': !isProductChecked(item.type), 'italic text-gray-500': item.type === NO_PRODUCT_KEY }">
              <td class="px-2 py-2 border text-center">
                <input type="checkbox" class="form-checkbox h-4 w-4 cursor-pointer" :checked="isProductChecked(item.type)" @change="toggleProduct(item.type)" />
              </td>
              <td class="px-4 py-2 border font-medium" :class="item.type === NO_PRODUCT_KEY ? '' : 'text-gray-800'">
                {{ item.name }}
                <span v-if="item.type !== NO_PRODUCT_KEY && item.name !== item.type" class="text-xs text-gray-500 ml-1">({{ item.type }})</span>
                <!-- orders in the board that contain this product, all stages -->
                <span class="text-xs text-gray-400 ml-1">· {{ item.orderCount }} {{ $t('order_log.order_count_orders') }}</span>
              </td>
              <td class="px-4 py-2 border text-center">{{ item.type === NO_PRODUCT_KEY ? '–' : item.inStock }}</td>
              <td class="px-4 py-2 border text-center">{{ item.type === NO_PRODUCT_KEY ? '–' : item.ordered }}</td>
              <td
                class="px-4 py-2 border text-center font-semibold"
                :class="{
                  'text-red-600': item.type !== NO_PRODUCT_KEY && item.remaining < 0,
                  'text-green-600': item.type !== NO_PRODUCT_KEY && item.remaining >= 0
                }"
              >
                {{ item.type === NO_PRODUCT_KEY ? '–' : item.remaining }}
              </td>
            </tr>
          </tbody>
        </table>
        <div class="flex flex-wrap items-center text-xs text-gray-600 mt-1 px-1">
          <button type="button" class="text-primary-500 hover:underline mr-3" @click.prevent="toggleAllProducts(false)">{{ $t('order_log.uncheck_all_products') }}</button>
          <button type="button" class="text-primary-500 hover:underline" @click.prevent="toggleAllProducts(true)">{{ $t('order_log.check_all_products') }}</button>
          <span v-if="unchecked_product_types.length > 0" class="ml-3 italic">{{ $t('order_log.product_filter_active') }}</span>
        </div>
      </div>

      <div class="flex flex-col">
        <div class="w-full bg-action-bar px-2 py-2 clearfix">
          <!-- shown vs total: shown is what the kanban columns currently hold after filtering -->
          <div class="float-left flex items-center mx-2 mt-2 lg:mt-0 text-sm text-gray-700 h-full py-1">
            <span class="font-bold mr-1">{{ shownOrderCount }}</span>
            <span v-if="hasActiveFilter">{{ $t('order_log.order_count_of', { total: totalOrderCount }) }}</span>
            <span v-else>{{ $t('order_log.order_count_orders') }}</span>
          </div>
          <div class="float-right flex flex-wrap">
            <div class="mx-2 mt-2 lg:mt-0">
              <button class="btn-blue-outline mx-1 mt-2 lg:mt-0" @click="getAdminOrderAlarmLog()">
                <BaseIcon icon="undo" />
              </button>
            </div>
            <div class="select-wrapper mx-2 mt-2 lg:mt-0">
              <select class="bg-gray-100 rounded w-full text-sm text-gray-900 focus:outline-none border-b-2 border-gray-300 transition duration-500 px-1 py-1" v-model="selectedCountry" @change="filterByCustomer()">
                <option value="all">{{ $t("order_log.country_all_orders") }}</option>
                <option value="se">{{ $t("order_log.country_sweden") }}</option>
                <option value="no">{{ $t("order_log.country_norway") }}</option>
                <option value="nl">{{ $t("order_log.country_netherlands") }}</option>
              </select>
            </div>
            <div class="select-wrapper mx-2 mt-2 lg:mt-0">
              <select class="bg-gray-100 rounded w-full text-sm text-gray-900 focus:outline-none border-b-2 border-gray-300 transition duration-500 px-1 py-1" v-model="selected_customer" @change="filterByCustomer()">
                <option value="">{{ $t("order_log.all_customers") }}</option>
                <option v-for="c in customers" :key="c.customer_id" :value="c.customer_id">{{ c.name }}</option>
              </select>
            </div>
            <div class="select-wrapper mx-2 mt-2 lg:mt-0">
              <select class="bg-gray-100 rounded w-full text-sm text-gray-900 focus:outline-none border-b-2 border-gray-300 transition duration-500 px-1 py-1" v-model="selected_alarm_center" @change="filterByCustomer()">
                <option value="">{{ $t("order_log.all_alarm_centers") }}</option>
                <optgroup v-for="group in alarmCenterFilterGroups" :key="group.country" :label="group.label">
                  <option v-for="ac in group.centres" :key="ac._id" :value="ac._id">{{ ac.name }}</option>
                </optgroup>
              </select>
            </div>
            <!-- /staffs/support is admin-only, so sellers never see this filter.
                 Multi-select: several support staff can be ticked at once. -->
            <div class="relative mx-2 mt-2 lg:mt-0" v-if="user.role === 'admin'" ref="assigneeDropdown">
              <button type="button" class="bg-gray-100 rounded text-sm text-gray-900 focus:outline-none border-b-2 border-gray-300 transition duration-500 px-2 py-1 flex items-center" @click.prevent="assigneeDropdownOpen = !assigneeDropdownOpen">
                <span v-if="selected_assignees.length === 0">{{ $t("order_log.all_support_staff") }}</span>
                <span v-else>{{ $t("order_log.support_staff") }} ({{ selected_assignees.length }})</span>
                <BaseIcon :icon="assigneeDropdownOpen ? 'chevron-up' : 'chevron-down'" class="ml-2 text-xs" />
              </button>
              <div v-show="assigneeDropdownOpen" class="absolute right-0 mt-1 w-56 bg-white rounded shadow-lg border border-gray-200 z-50 py-2">
                <label v-for="s in support_staff" :key="s._id" class="flex items-center px-3 py-1 text-sm text-gray-800 hover:bg-gray-100 cursor-pointer">
                  <input type="checkbox" class="form-checkbox h-4 w-4" :value="s._id" v-model="selected_assignees" @change="filterByCustomer()" />
                  <span class="ml-2">{{ s.name }}</span>
                </label>
                <div v-if="support_staff.length === 0" class="px-3 py-1 text-sm italic text-gray-500">{{ $t("order_log.no_assignee") }}</div>
                <div class="border-t border-gray-200 mt-1 pt-1 px-3">
                  <button type="button" class="text-xs text-primary-500 hover:underline disabled:opacity-50" :disabled="selected_assignees.length === 0" @click.prevent="clearAssigneeFilter()">{{ $t("order_log.clear_selection") }}</button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="w-full">
          <div class="flex">
            <div class="min-h-screen flex overflow-x-scroll py-12">
              <div v-for="column in columns" :key="column.id" class="bg-gray-50 rounded-lg px-3 py-3 column-width mr-4">
                <p class="text-gray-700 font-semibold font-sans tracking-wide text-sm">{{ column.name }}</p>
                <Draggable :id="column.id" :list="column.order" :animation="200" :emptyInsertThreshold="100" :handle="column.noDrag" :disabled="column.noDrag" :sort="false" :move="moveElement" @end="saveDragOrder" ghost-class="ghost-card" group="order" class="h-full">
                  <div v-for="o in column.order" :key="o._id" class="bg-white shadow rounded px-3 pt-3 pb-5 border border-white mt-3" v-bind:class="{ 'cursor-move': !column.noDrag }">
                    <div class="flex justify-between items-start">
                      <div class="flex items-start">
                        <img :src="getCountryFlagSrc(getOrderCountry(o))" class="w-6 h-4 object-cover rounded-sm mt-1 flex-shrink-0" :alt="`${getOrderCountry(o).toUpperCase()} flag`" />
                        <div class="ml-2 flex-1">
                          <p v-if="o.process_order" class="text-gray-700 font-semibold font-sans tracking-wide text-sm hover:text-gray-800">
                            <span class="cursor-pointer" @click.prevent="showAdminOrderLogModal(o)">{{ getCustomerDisplayName(o) }} - {{ getUnitDisplayName(o) }}</span>
                            <span class="cursor-pointer" @click.prevent="showAdminOrderAlarmModal(o)">
                              <BaseIcon icon="pencil-alt" class="text-xxs ml-2 text-primary-500" />
                            </span>
                          </p>
                          <p v-else class="text-gray-700 font-semibold font-sans tracking-wide text-sm hover:text-gray-800" @click.prevent="showAdminOrderAlarmModal(o)">{{ getCustomerDisplayName(o) }} - {{ getUnitDisplayName(o) }}</p>
                        </div>
                      </div>
                    </div>
                    <div class="w-full mt-1">
                      <span class="text-xs text-gray-600 mr-3 font-bold">{{ $t('order_log.support_staff') }}</span>
                    </div>
                    <div class="w-full">
                      <span v-if="o.assignee_name" class="inline-block bg-blue-100 text-blue-800 rounded-full px-2 py-0.5 text-xs font-semibold">
                        {{ o.assignee_name }}
                      </span>
                      <span v-else class="text-sm text-gray-600 mr-1">{{ $t('order_log.no_assignee') }}</span>
                    </div>
                    <div class="w-full">
                      <span class="text-xs text-gray-600 mr-3 font-bold">{{ $t('order_log.created') }}</span>
                    </div>
                    <div class="w-full">
                      <span class="text-sm text-gray-600 mr-1">{{ formatDate(o.createdAt) }}</span>
                    </div>
                    <div class="w-full">
                      <span class="text-xs text-gray-600 mr-3 font-bold">{{ $t('order_log.last_modified') }}</span>
                    </div>
                    <div class="w-full">
                      <span class="text-sm text-gray-600 mr-1">{{ formatDate(o.updatedAt) }}</span>
                    </div>
                    <div class="w-full">
                      <span class="text-xs text-gray-600 mr-3 font-bold">{{ $t('order_log.expected_completion') }}</span>
                    </div>
                    <div class="w-full">
                      <span class="text-sm text-gray-600 mr-1">{{ o.delivery_period }}</span>
                    </div>
                    <div class="w-full mt-2">
                      <span class="text-xs text-gray-600 mr-3 font-bold">{{ $t('order_log.alarm_center') }}</span>
                    </div>
                    <div class="w-full">
                      <span class="text-sm text-gray-600 mr-1">{{ cardAlarmCenterLabel(o) }}</span>
                    </div>
                    <template v-if="hasShippingWarning(o)">
                      <div class="w-full mt-2 flex items-center">
                        <div class="w-full">
                          <span class="text-xs text-gray-600 mr-1 font-bold block">{{ $t('admin_order_alarm.delivery_method') }}</span>
                        </div>
                        <div>
                          <BaseIcon icon="exclamation-triangle" class="text-yellow-500 text-sm" :title="$t('order_log.shipping_warning')" />
                        </div>
                      </div>
                    </template>
                    <template v-else>
                      <!-- demo feedback: the delivery method replaced the
                           yes/no shipping question; older orders show the old
                           answer instead -->
                      <!-- label and value on their own rows like the other
                           fields, so "Levereras till ansvarig säljare" fits
                           on one line (no right margin: it needs 168 of 170px) -->
                      <div class="w-full mt-2">
                        <span class="text-xs text-gray-600 mr-3 font-bold">{{ $t('admin_order_alarm.delivery_method') }}</span>
                      </div>
                      <div class="w-full">
                        <span class="text-sm text-gray-600">{{ deliveryMethodLabel(o) }}</span>
                      </div>
                      <div class="w-full break-words" v-if="o.direct_shipping_address">
                        <span class="text-xs text-gray-600 italic">{{ o.direct_shipping_address }}</span>
                      </div>
                      <div class="w-full break-words" v-if="o.delivery_tracking_link">
                        <span class="text-xs text-gray-600 mr-1 font-bold">{{ $t('admin_order_alarm.delivery_tracking_link') }}:&nbsp;</span>
                        <span class="text-xs text-gray-600 italic">{{ o.delivery_tracking_link }}</span>
                      </div>
                    </template>
                    <div class="w-full mt-2" v-if="o.products && o.products.length">
                      <span class="text-xs text-gray-600 mr-3 font-bold">{{ $t('order_log.products') }}</span>
                      <div v-for="(line, idx) in cardProductLines(o)" :key="idx">
                        <span class="text-sm text-gray-600">{{ line.label }} × {{ line.qty }}</span>
                      </div>
                    </div>
                    <div class="flex mt-4 justify-between items-center">
                      <div class="px-3 h-6 rounded-full text-xs font-semibold flex items-center" :class="checkDateStatus(o.delivery_period )">
                        <span v-if="o.process_order" @click.prevent="showAdminOrderLogModal(o)" class="cursor-pointer">
                          {{ o.agreement_number }}
                        </span>
                        <span v-else class="cursor-pointer" @click.prevent="showAdminOrderAlarmModal(o)">
                          <BaseIcon icon="pencil-alt" />
                        </span>
                      </div>
                    </div>
                  </div>
                </Draggable>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Draggable from "vuedraggable";
import AdminOrderAlarmModal from "@/components/modal/admin_order_alarm";
import AdminOrderAlarmLogModal from "@/components/modal/admin_order_alarm_log";
import PackingChecklistModal from "@/components/modal/order_packing_checklist";
import FlagSE from "@/assets/images/flags/se.svg";
import FlagNO from "@/assets/images/flags/no.svg";
import FlagNL from "@/assets/images/flags/nl.svg";
import { DUTCH_ORIGIN } from "@/utils/countryOrigin";

export default {
  name: "AdminOrderAlarmLog",
  components: {
    Draggable,
    AdminOrderAlarmModal,
    AdminOrderAlarmLogModal,
    PackingChecklistModal,
  },
  title() {
    return this.$t('page_titles.order');
  },
  data() {
    return {
      user: this.$store.state.user,
      id: this.$route.params.id,
      sim_operators: ["Telia", "Telia DCP", "Telenor", "Tele2", "Kundens Simkort"],
      customers: [],
      units: [],
      alarm_centers: [],
      // SEC-405: every country's alarm centres, for an admin's board
      all_alarm_centers: [],
      sellers: [],
      logs: [],
      selected_customer: "",
      selected_alarm_center: "",
      selected_assignees: [],
      support_staff: [],
      assigneeDropdownOpen: false,
      // product types unticked in the stock table; empty = no product filter
      unchecked_product_types: [],
      // pseudo product row for orders that have no product lines at all, so
      // every order belongs to at least one row and the counts add up
      NO_PRODUCT_KEY: "__no_products__",
      selectedCountry: "all",
      log_info: null,
      // update form
      customer: null,
      unit: null,
      updateInfo: null,
      // "Packas" (packing) is shown before "Kontroll"
      // (quality_control) to match the real workflow — presentation only.
      columns: [
        { name: this.$t('order_log.pending'), id: "pending", noDrag: false, order: [] },
        { name: this.$t('order_log.ordering'), id: "order_created", noDrag: false, order: [] },
        { name: this.$t('order_log.create_alarm'), id: "create_alarm", noDrag: false, order: [] },
        { name: this.$t('order_log.config'), id: "config", noDrag: false, order: [] },
        { name: this.$t('order_log.test_alarm'), id: "test_alarm", noDrag: false, order: [] },
        { name: this.$t('order_log.packing'), id: "packing", noDrag: false, order: [] },
        { name: this.$t('order_log.quality_control'), id: "quality_control", noDrag: false, order: [] },
        { name: this.$t('order_log.complete'), id: "complete", noDrag: false, order: [] },
      ],
      stockCounts: {},
      comparison: {},
      archive: false,
      archivedOrders: [],
      archiveSearchTerm: "",
      archiveLoading: false,
      archivePagination: { page: 1, limit: 20, total: 0, totalPages: 0 },
      exportLoading: false,
      pendingChecklistMove: null,
      customerNameCache: {},
      countryFlags: {
        se: FlagSE,
        no: FlagNO,
        nl: FlagNL,
      },
      archivedColumns: [
        {
          label: this.$t('order_log.customer'),
          field: "customer_name", // Changed to use pre-computed name
        },
        {
          label: this.$t('order_log.business'),
          field: "unit_display_name", // Pre-computed associated business/unit name
          formatFn: val => {
            const MAX_LEN = 40;
            if (!val) return "";
            return val.length > MAX_LEN ? val.slice(0, MAX_LEN) + "…" : val;
          },
        },
        { label: this.$t('order_log.agreement'), field: "agreement_number" },
        { label: this.$t('order_log.delivery'), field: "delivery_period" },
        {
          label: this.$t('order_log.updated'),
          field: "updatedAt",
          formatFn: val => this.moment(val).format("YYYY-MM-DD")
        }
      ]
    };
  },

  methods: {
    async getAdminOrderAlarmLog() {
      try {
        let response = await this.axios.get(`${process.env.VUE_APP_SERVER_URL}/admin/unit/order-alarm/logs`);
        this.logs = Object.freeze(response.data.order_alarm_logs);
        this.filterByCustomer();
        this.compareStockToOrders(this.logs);
      } catch (error) {
        this.handleError(error);
      }
    },

generateOrderColumn(order_alarm_logs) {
    // packing before quality_control — must match data().columns
    const stageMap = {
      pending: 0, order_created: 1, create_alarm: 2, config: 3,
      test_alarm: 4, packing: 5, quality_control: 6, complete: 7,
    };

    let columns = [
      { name: this.$t('order_log.pending'), id: "pending", noDrag: false, order: [] },
      { name: this.$t('order_log.ordering'), id: "order_created", noDrag: false, order: [] },
      { name: this.$t('order_log.create_alarm'), id: "create_alarm", noDrag: false, order: [] },
      { name: this.$t('order_log.config'), id: "config", noDrag: false, order: [] },
      { name: this.$t('order_log.test_alarm'), id: "test_alarm", noDrag: false, order: [] },
      { name: this.$t('order_log.packing'), id: "packing", noDrag: false, order: [] },
      { name: this.$t('order_log.quality_control'), id: "quality_control", noDrag: false, order: [] },
      { name: this.$t('order_log.complete'), id: "complete", noDrag: false, order: [] },
    ];

    order_alarm_logs.forEach((order) => {
      const idx = stageMap[order.stage];
      if (idx !== undefined) {
        columns[idx].order.push(order);
      }
    });

    // Sort each column once after all orders have been added
    columns.forEach((column) => {
      column.order.sort((a, b) => {
        let dateA = this.moment(a.delivery_period, 'YYYY-MM-DD', true);
        let dateB = this.moment(b.delivery_period, 'YYYY-MM-DD', true);

        if (!dateA.isValid() && !dateB.isValid()) return 0;
        if (!dateA.isValid()) return 1;
        if (!dateB.isValid()) return -1;

        if (dateA.isBefore(this.todaysDate) && dateB.isAfter(this.todaysDate)) return -1;
        if (dateB.isBefore(this.todaysDate) && dateA.isAfter(this.todaysDate)) return 1;

        return dateA.diff(dateB);
      });
    });

    this.columns = columns;
    },

    // Products from the SE and NO portals share `type` but not name/_id, so
    // everything is keyed on type. Old order lines without a type fall back to
    // the name, which keeps them as their own row rather than dropping them.
    productTypeKey(product) {
      return product.type || product.name || "";
    },

    // Quantity of one order line. Current orders store variants as their own
    // top-level lines (same type, own qty); legacy orders nested them under
    // the parent with the parent's qty at 0, so nested variant qty is added.
    productLineQty(product) {
      let qty = Number(product.qty) || 0;
      if (Array.isArray(product.variants)) {
        product.variants.forEach((v) => { qty += Number(v.qty) || 0; });
      }
      return qty;
    },

    // Distinct product types an order contains (qty > 0). Orders with no
    // product lines at all get the pseudo key so they still have a row.
    orderProductTypes(order) {
      const types = new Set();
      (order.products || []).forEach((product) => {
        const type = this.productTypeKey(product);
        if (type && this.productLineQty(product) > 0) types.add(type);
      });
      if (types.size === 0) types.add(this.NO_PRODUCT_KEY);
      return types;
    },

    // Per type: ordered qty (stage "Beställning" only, that is what the stock
    // comparison is about), how many orders on the board contain it (all
    // stages), and one display name per portal.
    countDevices(order_alarm_logs) {
      const productCount = {};
      const ensure = (type) => {
        if (!productCount[type]) productCount[type] = { qty: 0, orderCount: 0, names: {} };
        return productCount[type];
      };

      order_alarm_logs.forEach(order => {
        const country = order.country || "se";
        const inCreated = order.stage === "order_created";

        this.orderProductTypes(order).forEach((type) => { ensure(type).orderCount++; });

        (order.products || []).forEach(product => {
          const type = this.productTypeKey(product);
          const qty = this.productLineQty(product);
          if (!type || qty <= 0) return;
          const entry = ensure(type);
          if (inCreated) entry.qty += qty;
          // the Swedish name becomes the row label when present
          if (!entry.names[country]) entry.names[country] = product.name || type;
        });
      });
      return productCount;
    },

    isProductChecked(type) {
      return !this.unchecked_product_types.includes(type);
    },

    toggleProduct(type) {
      const idx = this.unchecked_product_types.indexOf(type);
      if (idx >= 0) this.unchecked_product_types.splice(idx, 1);
      else this.unchecked_product_types.push(type);
      this.filterByCustomer();
    },

    toggleAllProducts(checked) {
      this.unchecked_product_types = checked ? [] : [...this.allProductTypes];
      this.filterByCustomer();
    },


    async getStockCounts() {
      try {
        let response = await this.axios.get(`${process.env.VUE_APP_SERVER_URL}/orders/stock-counts`);
        // The stock document's `type` is the device label ("TWIG Neo"), while an
        // order line's `type` is the product's system code ("twig_neo", derived
        // from the name in admin_products.vue). Key the counts on the system
        // code so the two match.
        const stockCounts = {};
        Object.entries(response.data.stockCounts || {}).forEach(([label, count]) => {
          const key = this.stockTypeKey(label);
          stockCounts[key] = (stockCounts[key] || 0) + count;
        });
        this.stockCounts = stockCounts;
        // logs and stock are fetched in parallel; whichever arrives last
        // fills the table
        if (this.logs.length) this.compareStockToOrders(this.logs);
      } catch (error) {
        this.handleError(error);
      }
    },

    // Same rule as the auto-generated product type in admin_products.vue
    stockTypeKey(label) {
      return String(label || "").trim().toLowerCase().replace(/\s+/g, "_");
    },

    async fetchArchivedOrders(page = 1) {
      try {
        this.archiveLoading = true;
        const params = {
          archived: true,
          page,
          limit: this.archivePagination.limit,
        };
        if (this.archiveSearchTerm && this.archiveSearchTerm.trim()) {
          params.search = this.archiveSearchTerm.trim();
        }
        if (this.selectedCountry && this.selectedCountry !== 'all') {
          params.country = this.selectedCountry;
        }
        if (this.selected_customer) {
          params.customer_id = this.selected_customer;
        }
        if (this.selected_assignees.length > 0) {
          params.assignee = this.selected_assignees.join(",");
        }

        let response = await this.axios.get(
          `${process.env.VUE_APP_SERVER_URL}/admin/unit/order-alarm/logs`,
          { params }
        );
        const orders = response.data.order_alarm_logs.map(order => ({
          ...order,
          customer_name: this.getCustomerDisplayNameCached(order),
          unit_display_name: this.getUnitDisplayName(order),
        }));
        this.archivedOrders = orders;
        this.archivePagination = response.data.pagination;
      } catch (error) {
        this.handleError(error);
      } finally {
        this.archiveLoading = false;
      }
    },

    compareStockToOrders(order_alarm_logs) {
      let ordered = this.countDevices(order_alarm_logs);
      let comparison = {};

      for (let [type, entry] of Object.entries(ordered)) {
        // stockCounts is keyed on the product system code, see getStockCounts
        let stock = this.stockCounts[type] || 0;
        comparison[type] = {
          type,
          name: type === this.NO_PRODUCT_KEY ? this.$t("order_log.no_products") : (entry.names.se || Object.values(entry.names)[0] || type),
          orderCount: entry.orderCount,
          inStock: stock,
          ordered: entry.qty,
          remaining: stock - entry.qty
        };
      }
      this.comparison = comparison;
    },

    getCustomers() {
      this.axios
        .get(`${process.env.VUE_APP_SERVER_URL}/customer/`)
        .then((response) => {
          this.customers = Object.freeze(response.data.data);
          this.buildCustomerNameCache();
          this.filterByCustomer();
        })
        .catch((error) => {
          this.handleError(error);
        });
    },

    buildCustomerNameCache() {
      this.customerNameCache = {};
      this.customers.forEach(customer => {
        this.customerNameCache[customer.customer_id] = customer.name;
      });
    },

    getUnits() {
      this.axios
        .get(`${process.env.VUE_APP_SERVER_URL}/units/`)
        .then((response) => {
          this.units = Object.freeze(response.data.data);
        })
        .catch((error) => {
          this.handleError(error);
        });
    },

    getSellersResellers() {
      this.axios
        .get(`${process.env.VUE_APP_SERVER_URL}/seller/reseller`)
        .then((response) => {
          this.sellers = response.data.data;
        })
        .catch((error) => {
          this.handleError(error);
        });
    },

    getAlarmCentres() {
      this.axios
        .get(`${process.env.VUE_APP_SERVER_URL}/alarm-central`)
        .then((response) => {
          this.alarm_centers = response.data.data;
        })
        .catch((error) => {
          this.handleError(error);
        });
    },

    // SEC-405: the board shows the orders of every country, but /alarm-central
    // answers with the portal's own centres only. An admin may ask for each
    // country (?country=), so the card and the filter know every centre.
    // The edit modal keeps the portal's list (alarm_centers above).
    async getAllAlarmCentres() {
      try {
        const [swedish, norwegian] = await Promise.all([
          this.axios.get(`${process.env.VUE_APP_SERVER_URL}/alarm-central`, { params: { country: "se" } }),
          this.axios.get(`${process.env.VUE_APP_SERVER_URL}/alarm-central`, { params: { country: "no" } }),
        ]);
        this.all_alarm_centers = [...swedish.data.data, ...norwegian.data.data];
      } catch (error) {
        this.handleError(error);
      }
    },

    // SEC-405: "Ingen" chosen in the order form is saved as "-", and Norway has
    // an alarm centre record called "Ingen alarmsentral". Both read "Ingen";
    // "–" means no alarm centre was set at all.
    cardAlarmCenterLabel(order) {
      if (order.alarm_center === "-") return this.$t("admin_order_alarm.none");
      const centre = _.find(this.boardAlarmCenters, { _id: order.alarm_center });
      if (!centre || !centre.name) return "–";
      if (centre.name.trim().toLowerCase() === "ingen alarmsentral") return this.$t("admin_order_alarm.none");
      return centre.name;
    },

    // SEC-405: the order's product lines, one per variant. An order saved from
    // the agreement form keeps the chosen variants inside the product (qty 0 on
    // the product itself) - Ajax products have variants - so each ordered
    // variant is listed as its own line.
    cardProductLines(order) {
      const lines = [];
      (order.products || []).forEach((p) => {
        const name = p.display_name || p.name;
        if (p.qty > 0) lines.push({ label: name, qty: p.qty });
        (p.variants || []).forEach((v) => {
          if (v.qty > 0) lines.push({ label: `${name} - ${v.name}`, qty: v.qty });
        });
      });
      return lines;
    },

    moveElement(evt) {
      let currentNode = evt.to;
      currentNode.style.opacity = 1;
      
      let nodes = ["order_created", "pending", "create_alarm", "config", "test_alarm", "quality_control", "packing", "complete"];
      nodes.forEach((id) => {
        if (currentNode.id != id) document.getElementById(id).style.opacity = 1;
        
      });
    },

    async saveDragOrder(evt) {
      let nodes = ["order_created", "pending", "create_alarm", "config", "test_alarm", "quality_control", "packing", "complete"];
      nodes.forEach((id) => {
        document.getElementById(id).style.opacity = 1;
      });

      try {
        let order = evt.item._underlying_vm_;

        let to = evt.to.id;
        let from = evt.from.id;
        if (to == from) return;

        // Every move into Färdig, from any column, requires a confirmed checklist.
        // Show the dialog instead of saving; confirm/cancel handlers finish the move.
        if (to === "complete") {
          this.pendingChecklistMove = { order };
          this.$modal.show("modal-packing-checklist");
          return;
        }

        let i = _.findIndex(this.columns, { id: to });
        if (i > -1) {
          this.columns[i].order = this.columns[i].order.filter((item) => item._id !== order._id);
          this.columns[i].order.unshift(order);
        }

        let data = {
          stage: to,
          pipedrive_deal_id: order.pipedrive_deal_id,
        };

        // eslint-disable-next-line
        let response = await this.axios.put(`${process.env.VUE_APP_SERVER_URL}/order-alarm/${order._id}`, data);
        this.getAdminOrderAlarmLog();
      } catch (error) {
        this.handleError(error);
      }
    },

    async confirmPackingChecklist(items) {
      const move = this.pendingChecklistMove;
      this.pendingChecklistMove = null;
      if (!move) return;

      try {
        await this.axios.put(`${process.env.VUE_APP_SERVER_URL}/order-alarm/${move.order._id}`, {
          stage: "complete",
          pipedrive_deal_id: move.order.pipedrive_deal_id,
          packing_checklist: { items },
        });
        this.handleSuccess(this.$t('order_log.moved_to_complete'));
      } catch (error) {
        this.handleError(error);
      } finally {
        this.getAdminOrderAlarmLog();
      }
    },

    cancelPackingChecklist() {
      this.pendingChecklistMove = null;
      // re-fetch so the optimistically moved card snaps back to its column
      this.getAdminOrderAlarmLog();
    },

    yesNoLabel(value) {
      if (value === "yes") return this.$t("yes");
      if (value === "no") return this.$t("no");
      return this.$t("order_log.not_specified");
    },

    // On the card, "no" and unanswered read the same ("Not specified") so
    // matching warning states don't show different texts; the detail and
    // edit views still show the exact stored value.
    // demo feedback: how the devices reach the customer. Orders saved before
    // the delivery method existed only have the yes/no answer.
    deliveryMethodLabel(order) {
      const labels = {
        salesperson: this.$t("admin_order_alarm.delivery_to_salesperson"),
        customer_pickup: this.$t("admin_order_alarm.delivery_customer_pickup"),
        recipient: this.$t("admin_order_alarm.delivery_to_recipient"),
      };
      if (order.delivery_method && labels[order.delivery_method]) return labels[order.delivery_method];
      if (order.direct_shipping === "yes") return labels.recipient;
      return this.$t("order_log.not_specified");
    },

    // warning triangle when the order does not say how the devices reach the
    // customer — no delivery method, and no answer from before it existed
    hasShippingWarning(order) {
      return !order.delivery_method && !order.direct_shipping;
    },

    exportArchivedOrders() {
      // let the user pick the file format first (Excel or CSV)
      this.$modal.show("modal-export-archive");
    },

    chooseExportFormat(format) {
      this.$modal.hide("modal-export-archive");
      this.doExportArchivedOrders(format);
    },

    async doExportArchivedOrders(format) {
      try {
        this.exportLoading = true;
        const params = { archived: true, export: true };
        if (this.archiveSearchTerm && this.archiveSearchTerm.trim()) {
          params.search = this.archiveSearchTerm.trim();
        }
        if (this.selectedCountry && this.selectedCountry !== "all") {
          params.country = this.selectedCountry;
        }
        if (this.selected_customer) {
          params.customer_id = this.selected_customer;
        }
        if (this.selected_assignees.length > 0) {
          params.assignee = this.selected_assignees.join(",");
        }

        const response = await this.axios.get(
          `${process.env.VUE_APP_SERVER_URL}/admin/unit/order-alarm/logs`,
          { params }
        );

        // Resolve labels and lookup tables once — with tens of thousands of
        // rows, per-row $t/moment calls and _.find scans freeze the UI.
        const labels = {
          customer: this.$t("order_log.customer"),
          business: this.$t("order_log.business"),
          agreement: this.$t("order_log.agreement"),
          assignee: this.$t("order_log.assignee"),
          delivery: this.$t("order_log.delivery"),
          status: this.$t("order_log.status"),
          delivery_method: this.$t("admin_order_alarm.delivery_method"),
          delivery_address: this.$t("order_log.delivery_address"),
          tracking_link: this.$t("admin_order_alarm.delivery_tracking_link"),
          created: this.$t("order_log.created"),
          updated: this.$t("order_log.updated"),
        };
        const unitNameById = {};
        this.units.forEach((u) => {
          unitNameById[u.unit_id] = u.name;
        });
        const dateOnly = (value) => (value ? String(value).slice(0, 10) : "");

        const rows = response.data.order_alarm_logs.map((order) => ({
          [labels.customer]: this.getCustomerDisplayNameCached(order),
          [labels.business]:
            unitNameById[order.unit_id] ||
            (order.customer_info && order.customer_info.unit_name) ||
            "",
          [labels.agreement]: order.agreement_number || "",
          [labels.assignee]: order.assignee_name || "",
          [labels.delivery]: order.delivery_period || "",
          [labels.status]: order.stage || "",
          [labels.delivery_method]: this.deliveryMethodLabel(order),
          [labels.delivery_address]: order.direct_shipping_address || "",
          [labels.tracking_link]: order.delivery_tracking_link || "",
          [labels.created]: dateOnly(order.createdAt),
          [labels.updated]: dateOnly(order.updatedAt),
        }));

        this.excelExport(rows, this.$t("order_log.archive"), "archived_orders", format);
      } catch (error) {
        this.handleError(error);
      } finally {
        this.exportLoading = false;
      }
    },

    clearAssigneeFilter() {
      this.selected_assignees = [];
      this.filterByCustomer();
    },

    closeAssigneeDropdownOnOutsideClick(event) {
      const el = this.$refs.assigneeDropdown;
      if (this.assigneeDropdownOpen && el && !el.contains(event.target)) this.assigneeDropdownOpen = false;
    },

    async getSupportStaff() {
      try {
        const response = await this.axios.get(`${process.env.VUE_APP_SERVER_URL}/staffs/support`);
        this.support_staff = response.data.data || [];
      } catch (error) {
        this.handleError(error);
      }
    },

    filterByCustomer() {
      let filtered = this.logs;

      // Filter by country
      if (this.selectedCountry && this.selectedCountry !== "all") {
        filtered = filtered.filter((order) => {
          const orderCountry = this.getOrderCountry(order);
          return orderCountry === this.selectedCountry;
        });
      }

      // Filter by customer
      if (this.selected_customer !== "") {
        filtered = filtered.filter((order) => order.customer_id === this.selected_customer);
      }

      // Filter by alarm center
      if (this.selected_alarm_center !== "") {
        filtered = filtered.filter((order) => order.alarm_center === this.selected_alarm_center);
      }

      // Filter by assigned support staff (same staff _id the assignee dropdown saves)
      if (this.selected_assignees.length > 0) {
        const selected = new Set(this.selected_assignees);
        filtered = filtered.filter((order) => selected.has(order.assignee));
      }

      // Filter by product: only active once something is unticked in the stock
      // table. An order stays if at least one of its product types is ticked
      // (orders without products count as the "no products" row); with
      // nothing ticked the board is empty.
      if (this.unchecked_product_types.length > 0) {
        const checked = new Set(this.checkedProductTypes);
        filtered = filtered.filter((order) => [...this.orderProductTypes(order)].some((type) => checked.has(type)));
      }

      this.generateOrderColumn(filtered);

      // Refetch archive if it's currently visible
      if (this.archive) {
        this.fetchArchivedOrders(1);
      }
    },

    getOrderCountry(order) {
      if (!order) return "se"; // Default to Sweden

      let country = "se"; // Default to Sweden
      // Use order's own country field first
      if (order.country) {
        country = order.country.toLowerCase();
      }
      // Fallback to customer's country
      else if (order.customer_id) {
        const customer = _.find(this.customers, { customer_id: order.customer_id });
        if (customer && customer.country) {
          country = customer.country.toLowerCase();
        }
      }

      // A Dutch customer's order (country_origin 'nl') is handled in the Swedish portal
      // but flagged and filtered as Dutch. The server marks it, on both portals' boards
      // (units/controllers/unit_controller.js markDutchCustomerOrders).
      if (country !== "no" && order.customer_country_origin === DUTCH_ORIGIN) return DUTCH_ORIGIN;
      return country;
    },

    getCountryFlagSrc(country) {
      if (!country) country = "se"; // Default to Sweden
      const normalizedCountry = country.toLowerCase();
      return this.countryFlags[normalizedCountry] || this.countryFlags["se"];
    },

    getCustomerName(customer_id) {
      let c = _.find(this.customers, { customer_id: customer_id });
      if (c) return c.name;
      else return "";
    },

    // Get customer display name with fallback to order's stored customer_info
    // Used for cross-country orders where customer is not in current site's customer list
    getCustomerDisplayName(order) {
      let c = _.find(this.customers, { customer_id: order.customer_id });
      if (c) return c.name;
      // Fallback to stored customer_info.company_name (for Norwegian orders viewed from Swedish site, etc.)
      if (order.customer_info && order.customer_info.company_name) {
        return order.customer_info.company_name;
      }
      return "";
    },

    getCustomerNameCached(customer_id) {
      // Use cache for O(1) lookup instead of O(n) _.find
      return this.customerNameCache[customer_id] || "";
    },

    // Cached version with fallback to stored customer_info
    getCustomerDisplayNameCached(order) {
      const cachedName = this.customerNameCache[order.customer_id];
      if (cachedName) return cachedName;
      // Fallback to stored customer_info.company_name
      if (order.customer_info && order.customer_info.company_name) {
        return order.customer_info.company_name;
      }
      return "";
    },

    getUnitName(unit_id) {
      let u = _.find(this.units, { unit_id: unit_id });
      if (u) return u.name;
      else return "";
    },

    // Get unit name with fallback to stored customer_info for cross-country orders
    getUnitDisplayName(order) {
      let u = _.find(this.units, { unit_id: order.unit_id });
      if (u) return u.name;
      // Fallback to stored unit_name for cross-country orders
      if (order.customer_info && order.customer_info.unit_name) {
        return order.customer_info.unit_name;
      }
      return "";
    },

    getSellerName(seller_id) {
      let s = _.find(this.sellers, { _id: seller_id });
      if (s) return s.name;
      else return "";
    },

    getAlarmCenterName(alarm_center_id) {
      let a = _.find(this.alarm_centers, { _id: alarm_center_id });
      if (a) return a.name;
      else return "";
    },

    showAdminOrderAlarmModal(log) {
      var vm = this;

      // Always overwrite: keeping the previous order's customer/unit when this
      // order has no match would show and prefill another order's data.
      let u = _.find(this.units, { unit_id: log.unit_id });
      let c = _.find(this.customers, { customer_id: log.customer_id });
      // redrawn orders may lack customer_id - fall back to the unit's parent
      if (!c && u && u.parent_id) c = _.find(this.customers, (cu) => String(cu._id) === String(u.parent_id));
      this.customer = c || null;
      this.unit = u || null;

      this.updateInfo = log;

      this.$nextTick().then(() => {
        vm.$refs.adminOrderAlarmUpdate.setupOrderForm();
      });
      this.$modal.show("modal-admin-order-alarm");
    },

    showAdminOrderLogModal(log) {
      this.log_info = log;
      this.$modal.show("modal-admin-order-alarm-log");
    },

    formatDate(date) {
      return this.moment(date).format("YYYY-MM-DD");
    },
    checkDateStatus(date) {
      let expirationDate = this.moment(date);
      let diff = (expirationDate.diff(this.todaysDate, "days"));
   
      if (diff <= 9 && diff > 2) {
        return 'bg-yellow-500 text-gray-100';
      } else if (diff >= 0 && diff <= 2) {
        return 'bg-red-600 text-gray-100';
      } else if (diff <= 1) {
        return 'bg-black text-gray-100';
      } else {
        return 'bg-primary-100'; 
      }
    },

    toggleArchive() {
      this.archive = !this.archive;
      if (this.archive && this.archivedOrders.length === 0) {
        this.fetchArchivedOrders(1);
      }
    },

    debouncedSearch() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.fetchArchivedOrders(1);
      }, 300);
    },
  },
  
  computed: {
    todaysDate() {
      return this.moment(new Date());
    },
    // SEC-405: the alarm centres the board's orders can have. The portal's own
    // list is used until every country's list has loaded, or if it failed.
    boardAlarmCenters() {
      if (this.user.role === "admin" && this.all_alarm_centers.length > 0) return this.all_alarm_centers;
      return this.alarm_centers;
    },
    // SEC-405: the alarm-centre filter, grouped per country and sorted by name
    alarmCenterFilterGroups() {
      const groups = [
        { country: "se", label: this.$t("country_origin.se"), centres: [] },
        { country: "no", label: this.$t("country_origin.no"), centres: [] },
      ];
      this.boardAlarmCenters.forEach((centre) => {
        const group = centre.country === "no" ? groups[1] : groups[0];
        group.centres.push(centre);
      });
      groups.forEach((group) => {
        group.centres.sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""), "sv"));
      });
      return groups.filter((group) => group.centres.length > 0);
    },
    stockList() {
      return Object.values(this.comparison).sort((a, b) => {
        if (a.type === this.NO_PRODUCT_KEY) return 1;
        if (b.type === this.NO_PRODUCT_KEY) return -1;
        return a.name.localeCompare(b.name);
      });
    },
    allProductTypes() {
      return Object.keys(this.comparison);
    },
    checkedProductTypes() {
      return this.allProductTypes.filter((type) => !this.unchecked_product_types.includes(type));
    },
    filteredArchivedOrders() {
      return this.archivedOrders;
    },
    // Orders currently in the kanban columns (after all filters)
    shownOrderCount() {
      return this.columns.reduce((sum, column) => sum + column.order.length, 0);
    },
    // Everything loaded for the board, before filtering
    totalOrderCount() {
      return this.logs.length;
    },
    hasActiveFilter() {
      return (this.selectedCountry && this.selectedCountry !== "all") || this.selected_customer !== "" || this.selected_alarm_center !== "" || this.selected_assignees.length > 0 || this.unchecked_product_types.length > 0;
    }
  },
  mounted() {
    document.addEventListener("click", this.closeAssigneeDropdownOnOutsideClick);
  },
  beforeDestroy() {
    document.removeEventListener("click", this.closeAssigneeDropdownOnOutsideClick);
  },
  created() {
    this.getCustomers();
    this.getUnits();
    this.getSellersResellers();
    this.getAlarmCentres();
    if (this.user.role === "admin") this.getAllAlarmCentres();
    if (this.user.role === "admin") this.getSupportStaff();
    this.getAdminOrderAlarmLog();
    this.getStockCounts();
    this.setPageTitle(this.$t('order_log.page_title'), "admin_order_alarm_log");
  },
};
</script>
<style scoped>
.modal-export-archive ::v-deep .modal-body {
  padding: 0 25px 40px 25px;
}
</style>